/**
 * 止めの画面（#186、2026-10-15 射場さんの決定、docs/75。Figma「招待と会社の追加（10/14）」⑤-1〜4）
 *   先へ進めないときの1枚。型は1つ：ロゴ（製品で切替）／絵（132px の丸い地に簡単な形）／題（24px 太字）／本文（16px）／
 *   補足（14px 薄墨・任意）／主ボタン1つ＋脇ボタン1つまで。場面ごとに変わるのは 絵・文・ボタンだけ＝余白・文字・ボタンは全場面で同じ。
 *   スマホ（スマホ版・幅 767px 以下）は縦1列でボタンは画面の下、PC は中央に 560px の列（styles.css の .stop-screen）。
 *   場面の部品（下の PcOnlyStop … AdminOnlyStop）は文を contract の STOP_MSG から渡すだけ。新しい場面もここに足す（中身を画面ごとに書かない）
 *
 *   a) PcOnlyStop        PC 専用の機能（contract の PC_ONLY_FEATURES）をスマホで開いた（ルートの守り＝app.tsx の PcOnlyGuard）
 *   b) InviteStop        招待の有効期限切れ（と、無効・取り消し・使用済み）＝pages/Invite.tsx
 *   c) ReadOnlyStop      無料トライアルが終わり閲覧のみの会社が、作成の画面を開いた＝app.tsx
 *   d) NotFoundStop      ページが見つからない（どのルートにも当たらない道）＝app.tsx
 *   e) MaintenanceStop   メンテナンス中（API の 503 MAINTENANCE。管理アプリの案内の停止期間か .env の MAINTENANCE_UNTIL）＝app.tsx の MaintenanceGate
 *   f) AdminOnlyStop     管理者だけの画面を担当者が開いた＝app.tsx
 *   g) IdleStop          自動ログアウト（#189、docs/76）：API の 401 SESSION_IDLE＝components/AutoLogout.tsx が画面全体に重ねる
 *   h) CrashStop         画面の表示中の例外・部品の読み込みの失敗（#189 A）＝components/ErrorBoundary.tsx（真っ白にしない）
 *   i) TrashedStop       ゴミ箱の見積書を開いた（#200、docs/81。API の 404 ESTIMATE_TRASHED）＝pages/Estimate.tsx・phone/PhoneEstimate.tsx
 */
import { STOP_MSG, loginPathWithNext, maintenanceEndText, type TrashedInfo } from "@genbaryoku/contract";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useProduct } from "../lib/useProduct.js";
import { usePageName } from "./BackButton.jsx";
import { notify } from "./Notice.jsx";
import { useLogoHeader } from "./LightArea.jsx";
import { fmtDateTimeShort } from "../lib/dates.js";

export type StopArt = "pc" | "clock" | "mail" | "readonly" | "question" | "tool" | "lock" | "reload" | "trash";

/** ボタン1つ：to＝画面の中の道／href＝ページごと移る（ダウンロード・ログイン前の画面）／onClick */
export type StopAction = { label: string; to?: string; href?: string; download?: boolean; onClick?: () => void; testId?: string };

function Art({ kind }: { kind: StopArt }) {
  if (kind === "readonly") return <span className="stop-art-text">{STOP_MSG.readOnly.art}</span>;
  if (kind === "question") return <span className="stop-art-q">？</span>;
  return (
    <svg viewBox="0 0 132 132" width="132" height="132" aria-hidden="true">
      {kind === "pc" ? (
        <>
          <rect x="12" y="38" width="66" height="44" rx="3" className="fill-paper stroke-ai" strokeWidth="4" />
          <rect x="40" y="84" width="10" height="8" className="fill-ai" />
          <rect x="28" y="92" width="34" height="4" rx="2" className="fill-ai" />
          <rect x="82" y="48" width="20" height="36" rx="3" className="fill-paper stroke-shu" strokeWidth="3" />
          <path d="M107 60l11 11M118 60l-11 11" className="stroke-shu" strokeWidth="3" strokeLinecap="round" />
        </>
      ) : kind === "clock" ? (
        <>
          <circle cx="66" cy="66" r="30" className="fill-paper stroke-ai" strokeWidth="4" />
          <path d="M66 66V46M66 66h14" className="stroke-ai" strokeWidth="4" strokeLinecap="round" />
        </>
      ) : kind === "mail" ? (
        <>
          <rect x="34" y="44" width="64" height="44" rx="4" className="fill-paper stroke-ai" strokeWidth="4" />
          <path d="M36 48l30 22 30-22" className="stroke-ai" strokeWidth="4" strokeLinejoin="round" fill="none" />
        </>
      ) : kind === "reload" ? (
        <>
          {/* 読み込み直し（#189 A）：丸い矢印 */}
          <path d="M92 66a26 26 0 1 1-7.6-18.4" className="stroke-ai" strokeWidth="4" strokeLinecap="round" fill="none" />
          <path d="M86 36v12H74" className="stroke-ai" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </>
      ) : kind === "trash" ? (
        <>
          {/* ゴミ箱（#200）：ふたと箱 */}
          <path d="M42 46h48M58 46v-6h16v6" className="stroke-ai" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <path d="M47 52l4 42a4 4 0 0 0 4 3.6h22a4 4 0 0 0 4-3.6l4-42z" className="fill-paper stroke-ai" strokeWidth="4" strokeLinejoin="round" />
          <path d="M59 62v24M73 62v24" className="stroke-ai" strokeWidth="4" strokeLinecap="round" />
        </>
      ) : kind === "lock" ? (
        <>
          <path d="M52 62V52a14 14 0 0 1 28 0v10" className="stroke-ai" strokeWidth="4" fill="none" />
          <rect x="44" y="62" width="44" height="34" rx="4" className="fill-paper stroke-ai" strokeWidth="4" />
          <circle cx="66" cy="76" r="4" className="fill-ai" />
          <rect x="64.5" y="78" width="3" height="9" rx="1.5" className="fill-ai" />
        </>
      ) : (
        // tool：スパナ（メンテナンス）
        <path
          d="M77 48.3a3 3 0 0 0 0 4.2l4.5 4.5a3 3 0 0 0 4.2 0l9.6-9.6a16 16 0 0 1-21.2 21.2L56 86.7a5.6 5.6 0 0 1-8-8l18.1-18.1a16 16 0 0 1 21.2-21.2z"
          className="fill-paper stroke-ai"
          strokeWidth="4"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function ActionButton({ a, primary }: { a: StopAction; primary: boolean }) {
  const cls = primary ? "btn primary" : "btn";
  if (a.to) {
    return (
      <Link className={cls} to={a.to} replace data-testid={a.testId}>
        {a.label}
      </Link>
    );
  }
  if (a.href) {
    return (
      <a className={cls} href={a.href} download={a.download || undefined} data-testid={a.testId}>
        {a.label}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={a.onClick} data-testid={a.testId}>
      {a.label}
    </button>
  );
}

export function StopScreen({
  art,
  title,
  body,
  note,
  primary,
  secondary,
  id,
  testId = "stop",
}: {
  art: StopArt;
  title: string;
  /** 本文（空なら出さない＝h の画面は題とボタンだけ） */
  body: string;
  note?: string | undefined;
  /** 無ければボタンを出さない（メールの閲覧ページ＝受け取る人には行き先が無い。#225） */
  primary?: StopAction | undefined;
  secondary?: StopAction | undefined;
  id?: string;
  testId?: string;
}) {
  const P = useProduct();
  const logoHeader = useLogoHeader(P.product); // ダークでは暗い地用（#191 の2回目）
  const h = useRef<HTMLHeadingElement>(null);
  usePageName(title);
  // 読み上げが新しい画面の題から読む（ボタンには移さない＝Enter で押してしまわない）
  useEffect(() => {
    h.current?.focus({ preventScroll: true });
  }, [title]);
  return (
    <div className="stop-screen" id={id} data-testid={testId} data-product={P.product}>
      <div className="stop-top">
        {/* 高さも書く（#179：ロゴが届くまで高さ 0 にしない。横組みのロゴは 320:100） */}
        <img className="stop-logo" src={logoHeader} alt={P.name} width={128} height={40} />
        <div className="stop-art" data-art={art}>
          <Art kind={art} />
        </div>
        <h1 className="stop-title" ref={h} tabIndex={-1}>
          {title}
        </h1>
        {body ? <p className="stop-body">{body}</p> : null}
        {note ? <p className="stop-note">{note}</p> : null}
      </div>
      {/* スマホは画面の下（通知はこの上に出る＝data-notice-above）。PC は文のすぐ下 */}
      {primary || secondary ? (
        <div className="stop-acts" data-notice-above>
          {primary ? <ActionButton a={primary} primary /> : null}
          {secondary ? <ActionButton a={secondary} primary={false} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/** いまの URL をクリップボードへ（navigator.clipboard が無い・断られた端末は、選んで写す古い道） */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 下の古い道へ
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** a) PC 専用の機能をスマホで開いた */
export function PcOnlyStop() {
  const m = STOP_MSG.pcOnly;
  return (
    <StopScreen
      art="pc"
      title={m.title}
      body={m.body}
      note={m.note}
      primary={{ label: m.primary, to: "/home", testId: "stop-home" }}
      secondary={{
        label: m.copy,
        testId: "stop-copy-url",
        onClick: () =>
          void copyText(window.location.href).then((ok) => notify(ok ? { text: m.copied, testId: "stop-copied" } : { text: m.copyFailed, tone: "error", testId: "stop-copied" })),
      }}
      testId="stop-pc-only"
    />
  );
}

/** b) 招待のリンク：期限切れ（と、無効・取り消し・使用済み。同じ役割） */
export function InviteStop({ state }: { state: "expired" | "invalid" | "cancelled" | "used" }) {
  const m = state === "expired" ? STOP_MSG.inviteExpired : state === "cancelled" ? STOP_MSG.inviteCancelled : state === "used" ? STOP_MSG.inviteUsed : STOP_MSG.inviteInvalid;
  return <StopScreen art={state === "expired" ? "clock" : "mail"} title={m.title} body={m.body} primary={{ label: m.primary, to: "/login", testId: "stop-login" }} id={state} testId={`stop-invite-${state}`} />;
}

/** c) 閲覧のみ（無料トライアルの終了）。データの持ち出しは管理者だけ（API の requireAdmin と同じ） */
export function ReadOnlyStop({ admin }: { admin: boolean }) {
  const m = STOP_MSG.readOnly;
  return (
    <StopScreen
      art="readonly"
      title={m.title}
      body={m.body}
      primary={{ label: m.primary, to: "/home", testId: "stop-home" }}
      secondary={admin ? { label: m.export, href: "/api/v1/account/export", download: true, testId: "stop-export" } : undefined}
      testId="stop-read-only"
    />
  );
}

/** d) ページが見つからない */
export function NotFoundStop() {
  const m = STOP_MSG.notFound;
  return <StopScreen art="question" title={m.title} body={m.body} primary={{ label: m.primary, to: "/home", testId: "stop-home" }} testId="stop-not-found" />;
}

/**
 * e) メンテナンス中。endsAt・note・overdue は API の 503 の答え（docs/95）：本文＝終了予定、補足＝案内の本文の1行目（何の保守か）。
 * 終了予定を過ぎて DB がまだ戻らないあいだ（overdue）は本文を「終了予定の時刻を過ぎましたが、まだ作業中です。…」に
 */
export function MaintenanceStop({ endsAt, note = null, overdue = false }: { endsAt: string | null; note?: string | null; overdue?: boolean }) {
  const m = STOP_MSG.maintenance;
  return (
    <StopScreen
      art="tool"
      title={m.title}
      body={overdue ? m.overdue : m.body(maintenanceEndText(endsAt))}
      note={note ?? undefined}
      primary={{ label: m.primary, onClick: () => window.location.reload(), testId: "stop-reload" }}
      testId="stop-maintenance"
    />
  );
}

/** f) 管理者だけの画面を担当者が開いた */
export function AdminOnlyStop() {
  const m = STOP_MSG.adminOnly;
  return <StopScreen art="lock" title={m.title} body={m.body} primary={{ label: m.primary, to: "/home", testId: "stop-home" }} testId="stop-admin-only" />;
}

/**
 * g) 自動ログアウト（#189、docs/76）。ボタンはログインの画面へ（ログインのあとは path＝切れたときの画面へ戻る）。
 * ページごと移る（新しい版の画面を読む・この画面の控えを残さない）。書きかけの入力は切れたときに端末へ退避してある
 */
export function IdleStop({ path, onLogin }: { path: string; onLogin?: () => void }) {
  const m = STOP_MSG.idle;
  const to = loginPathWithNext(path);
  return (
    <StopScreen
      art="clock"
      title={m.title}
      body={m.body}
      primary={{ label: m.primary, testId: "stop-login", onClick: () => (onLogin ? onLogin() : window.location.assign(to)) }}
      testId="stop-idle"
    />
  );
}

/**
 * j) 会社の選択でログインの有効期限が切れた（#203 の続き、2026-10-17 夜）。ログインの続き（/choose）で、どの会社を押しても進めないとき。
 * ボタンはログインの画面へ（履歴に残さない＝戻るで切れた画面に戻さない）
 */
export function LoginExpiredStop() {
  const m = STOP_MSG.loginExpired;
  return <StopScreen art="clock" title={m.title} body={m.body} primary={{ label: m.primary, onClick: () => window.location.replace("/login"), testId: "stop-login" }} testId="stop-login-expired" />;
}

/** h) 画面の表示中の例外・部品の読み込みの失敗（#189 A）。「再読込」でページを読み込み直す */
export function CrashStop() {
  const m = STOP_MSG.crashed;
  return <StopScreen art="reload" title={m.title} body={m.body} primary={{ label: m.primary, onClick: () => window.location.reload(), testId: "stop-reload" }} testId="stop-crashed" />;
}

/**
 * i) ゴミ箱の見積書を開いた（#200、docs/81）。主ボタン「復元」で戻して開き直す（onRestore は画面が渡す）。
 * 脇は見積書の一覧のゴミ箱のタブ（スマホ版は一覧＝ゴミ箱のタブはパソコンだけ）。番号のある見積書は消えない＝補足に1行
 */
export function TrashedStop({ info, onRestore, listTo = "/estimates?f=trash" }: { info: TrashedInfo; onRestore: () => void; listTo?: string }) {
  const m = STOP_MSG.trashed;
  return (
    <StopScreen
      art="trash"
      title={m.title}
      body={m.body(fmtDateTimeShort(info.deletedAt), info.deletedBy)}
      note={info.documentNo ? m.note : undefined}
      primary={{ label: m.primary, onClick: onRestore, testId: "stop-untrash" }}
      secondary={{ label: m.secondary, to: listTo, testId: "stop-to-trash" }}
      testId="stop-trashed"
    />
  );
}
